import type { Id } from '@contracts/common';
import type { PartnerLabel } from '@contracts/partners';
import type {
  AnalysisDTO, ContractStatus, Engagement, Level, ProjectKind, ProjectStatus, StakeholderType, Strategy,
} from '@contracts/projects';

/**
 * The stakeholder rules of projects & stakeholders spec §3.3, pure so the server's reads and the browser's
 * screens derive the same strategy and the same "cần tác động" flag from the same analysis.
 */

/**
 * The PMBOK power × interest grid: high power with high interest is managed closely; high power alone is kept
 * satisfied; high interest alone is kept informed; everything else is monitored. Unassessed until both are set.
 */
export function strategyOf(power: Level | null, interest: Level | null): Strategy {
  if (power === null || interest === null) return 'unassessed';
  if (power === 'high') return interest === 'high' ? 'manage_closely' : 'keep_satisfied';
  return interest === 'high' ? 'keep_informed' : 'monitor';
}

/** A stakeholder needs action when its current and desired engagement are both known and differ. */
export function needsAction(current: Engagement | null, desired: Engagement | null): boolean {
  return current !== null && desired !== null && current !== desired;
}

type EngagementPair = Pick<AnalysisDTO, 'engagementCurrent' | 'engagementDesired'>;

/** How many rows of a project need action: each stakeholder organisation, and each of its people, counts once. */
export function needsActionCount(rows: ReadonlyArray<{
  analysis: EngagementPair; participants: ReadonlyArray<{ analysis: EngagementPair }>;
}>): number {
  const flagged = (a: EngagementPair): number => (needsAction(a.engagementCurrent, a.engagementDesired) ? 1 : 0);
  return rows.reduce((sum, row) => sum + flagged(row.analysis)
    + row.participants.reduce((people, person) => people + flagged(person.analysis), 0), 0);
}

/**
 * The stakeholder type a partner's directory labels suggest when it is added to a project: the first of
 * investor, consultant, implementer it carries; a supplier implements; anything else is `other`.
 */
export function suggestType(labels: readonly PartnerLabel[]): StakeholderType {
  for (const type of ['investor', 'consultant', 'implementer'] as const) if (labels.includes(type)) return type;
  return labels.includes('supplier') ? 'implementer' : 'other';
}

/** A project's investors (its "Chủ đầu tư" column): the investor rows' partners, by Vietnamese name order. */
export function investorsOf<T extends { type: StakeholderType; partner: { id: Id; name: string } }>(
  rows: readonly T[],
): { id: Id; name: string }[] {
  return rows.filter((row) => row.type === 'investor')
    .map((row) => ({ id: row.partner.id, name: row.partner.name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'vi'));
}

export const STRATEGY_LABEL: Record<Strategy, string> = {
  manage_closely: 'Quản lý chặt', keep_satisfied: 'Giữ hài lòng', keep_informed: 'Thông tin đầy đủ',
  monitor: 'Theo dõi', unassessed: 'Chưa đánh giá',
};

/** The full type name (select, modal). The list's group heading for `other` is "Stakeholder khác". */
export const STAKEHOLDER_TYPE_LABEL: Record<StakeholderType, string> = {
  investor: 'Chủ đầu tư', consultant: 'Đơn vị tư vấn', implementer: 'Đơn vị triển khai', other: 'Khác',
};

/** The short type name (badges, legend, map quadrants). */
export const STAKEHOLDER_TYPE_SHORT: Record<StakeholderType, string> = {
  investor: 'Chủ đầu tư', consultant: 'Tư vấn', implementer: 'Triển khai', other: 'Khác',
};

export const PARTNER_LABEL_LABEL: Record<PartnerLabel, string> = {
  investor: 'Chủ đầu tư', consultant: 'Tư vấn', implementer: 'Triển khai', supplier: 'Nhà cung cấp', other: 'Khác',
};

export const LEVEL_LABEL: Record<Level, string> = { low: 'Thấp', medium: 'Trung bình', high: 'Cao' };

/** The short level (segmented controls, the grid's axes). */
export const LEVEL_SHORT: Record<Level, string> = { low: 'Thấp', medium: 'TB', high: 'Cao' };

export const ENGAGEMENT_LABEL: Record<Engagement, string> = {
  unaware: 'Chưa biết', resistant: 'Phản đối', neutral: 'Trung lập', supportive: 'Ủng hộ', leading: 'Dẫn dắt',
};

export const PROJECT_STATUS_LABEL: Record<ProjectStatus, string> = {
  active: 'Đang chạy', paused: 'Tạm dừng', closed: 'Đã đóng',
};

export const PROJECT_KIND_LABEL: Record<ProjectKind, string> = { contract: 'Hợp đồng', internal: 'Nội bộ' };

export const CONTRACT_STATUS_LABEL: Record<ContractStatus, string> = {
  draft: 'Nháp', signed: 'Đã ký', in_progress: 'Đang thực hiện', completed: 'Hoàn thành', cancelled: 'Đã hủy',
};
